export interface InvoiceLineItem {
  description: string;
  category: string;
  quantity: number;
  rate: number;
  cgstPercent: number;
  sgstPercent: number;
  /** Legacy single tax field — split into CGST/SGST when loading old invoices */
  taxPercent?: number;
}

export interface ComputedLineItem extends InvoiceLineItem {
  base: number;
  cgstAmt: number;
  sgstAmt: number;
  amount: number;
}

export interface InvoiceTotals {
  lineItems: ComputedLineItem[];
  subtotal: number;
  cgstTotal: number;
  sgstTotal: number;
  taxTotal: number;
  total: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function normalizeLineItem(it: Partial<InvoiceLineItem>): InvoiceLineItem {
  const legacyTax = Number(it.taxPercent) || 0;
  const cgst = it.cgstPercent != null ? Number(it.cgstPercent) : legacyTax / 2;
  const sgst = it.sgstPercent != null ? Number(it.sgstPercent) : legacyTax / 2;
  return {
    description: it.description || '',
    category: it.category || '',
    quantity: Number(it.quantity) || 0,
    rate: Number(it.rate) || 0,
    cgstPercent: cgst,
    sgstPercent: sgst,
  };
}

export function computeInvoiceTotals(
  items: Partial<InvoiceLineItem>[],
  discount = 0,
  shipping = 0,
): InvoiceTotals {
  let subtotal = 0;
  let cgstTotal = 0;
  let sgstTotal = 0;

  const lineItems = (items || []).map((raw) => {
    const it = normalizeLineItem(raw);
    const base = it.quantity * it.rate;
    const cgstAmt = (base * it.cgstPercent) / 100;
    const sgstAmt = (base * it.sgstPercent) / 100;
    subtotal += base;
    cgstTotal += cgstAmt;
    sgstTotal += sgstAmt;
    return {
      ...it,
      base: round2(base),
      cgstAmt: round2(cgstAmt),
      sgstAmt: round2(sgstAmt),
      amount: round2(base),
    };
  });

  const taxTotal = round2(cgstTotal + sgstTotal);
  const total = round2(subtotal + taxTotal - (Number(discount) || 0) + (Number(shipping) || 0));

  return {
    lineItems,
    subtotal: round2(subtotal),
    cgstTotal: round2(cgstTotal),
    sgstTotal: round2(sgstTotal),
    taxTotal,
    total,
  };
}

/** Dominant CGST/SGST rate across line items (for totals label). */
export function dominantTaxRate(items: ComputedLineItem[]): { cgst: number; sgst: number } {
  if (!items.length) return { cgst: 2.5, sgst: 2.5 };
  const first = items[0];
  return { cgst: first.cgstPercent, sgst: first.sgstPercent };
}
